import { useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Toolbar,
  Typography,
  useMediaQuery
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";

import { Dashboard } from "../pages/Dashboard";
import { ArchiveBrowser } from "../pages/ArchiveBrowser";
import { SearchCenter } from "../pages/SearchCenter";
import { ActivityStream } from "../pages/ActivityStream";
import { NotificationsCenter } from "../pages/NotificationsCenter";
import { Settings } from "../pages/Settings";
import { Statistics } from "../pages/Statistics";
import { DeveloperScaffolding } from "../pages/DeveloperScaffolding";
import { Profile } from "../pages/Profile";
import { ThemeModeSwitch } from "../components/ThemeModeSwitch";
import { Login } from "../pages/Login";
import { useAuthStore } from "../store/authStore";
import { useNotificationStore } from "../store/notificationStore";
import { keycloakLogout } from "../auth/keycloakClient";

const drawerWidth = 260;

const navItems = [
  { label: "Dashboard", path: "/", icon: <DashboardRoundedIcon /> },
  { label: "Statistiche", path: "/stats", icon: <InsightsRoundedIcon /> },
  { label: "Archivio", path: "/archive", icon: <StorageRoundedIcon /> },
  { label: "Ricerca", path: "/search", icon: <SearchRoundedIcon /> },
  { label: "Notifiche", path: "/notifications", icon: <NotificationsRoundedIcon /> },
  { label: "Audit", path: "/activity", icon: <HistoryRoundedIcon /> },
  { label: "Profilo", path: "/profile", icon: <PersonRoundedIcon /> },
  { label: "Impostazioni", path: "/settings", icon: <SettingsRoundedIcon /> },
  { label: "Sviluppatori", path: "/developers", icon: <CodeRoundedIcon /> }
];

const RESTRICTED_PATHS = new Set(["/activity", "/settings", "/developers"]);
const LEGAL_ONLY_ALLOWED_PATHS = new Set(["/archive", "/search", "/profile"]);

function isLegalAllowedPath(path: string): boolean {
  for (const allowedPath of LEGAL_ONLY_ALLOWED_PATHS) {
    if (path === allowedPath || path.startsWith(`${allowedPath}/`)) {
      return true;
    }
  }
  return false;
}

function NavigationList({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const canManageSettings = useAuthStore((state) => state.canManageSettings());
  const isLegalRestrictedUser = useAuthStore((state) => state.isLegalRestrictedUser());
  const visibleNavItems = isLegalRestrictedUser
    ? navItems.filter((item) => LEGAL_ONLY_ALLOWED_PATHS.has(item.path))
    : (canManageSettings ? navItems : navItems.filter((item) => !RESTRICTED_PATHS.has(item.path)));

  return (
    <List>
      {visibleNavItems.map((item) => {
        const selected = item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path);
        return (
          <ListItemButton
            key={item.path}
            selected={selected}
            onClick={() => {
              navigate(item.path);
              onNavigate?.();
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        );
      })}
    </List>
  );
}

function DesktopLayout() {
  const authRequired = useAuthStore((state) => state.authRequired);
  const authLoading = useAuthStore((state) => state.authLoading);
  const provider = useAuthStore((state) => state.provider);
  const username = useAuthStore((state) => state.username);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const enqueueNotification = useNotificationStore((state) => state.enqueue);
  const showAuthActions = authRequired === true && !authLoading;

  const handleLogout = () => {
    clearAuth("manual");
    if (provider === "keycloak") {
      void keycloakLogout();
      return;
    }
    enqueueNotification({ message: "Disconnessione completata.", severity: "info" });
  };

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            PEC Archiver Console
          </Typography>
          {showAuthActions && username ? (
            <Typography variant="body2" sx={{ mr: 2 }}>
              {username}
            </Typography>
          ) : null}
          {showAuthActions ? (
            <Button color="inherit" onClick={handleLogout} sx={{ mr: 1 }}>
              Esci
            </Button>
          ) : null}
          <ThemeModeSwitch />
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: "border-box" }
        }}
      >
        <Toolbar />
        <NavigationList />
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, mt: 8 }}>
        <Outlet />
      </Box>
    </Box>
  );
}

function MobileLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const authRequired = useAuthStore((state) => state.authRequired);
  const authLoading = useAuthStore((state) => state.authLoading);
  const provider = useAuthStore((state) => state.provider);
  const username = useAuthStore((state) => state.username);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const enqueueNotification = useNotificationStore((state) => state.enqueue);
  const canManageSettings = useAuthStore((state) => state.canManageSettings());
  const isLegalRestrictedUser = useAuthStore((state) => state.isLegalRestrictedUser());
  const visibleNavItems = isLegalRestrictedUser
    ? navItems.filter((item) => LEGAL_ONLY_ALLOWED_PATHS.has(item.path))
    : (canManageSettings ? navItems : navItems.filter((item) => !RESTRICTED_PATHS.has(item.path)));
  const current = visibleNavItems.findIndex((item) => item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path));
  const showAuthActions = authRequired === true && !authLoading;

  const handleLogout = () => {
    clearAuth("manual");
    if (provider === "keycloak") {
      void keycloakLogout();
      return;
    }
    enqueueNotification({ message: "Disconnessione completata.", severity: "info" });
  };

  return (
    <Box sx={{ pb: 7, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => setOpen(true)}>
            <MenuRoundedIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            PEC Archiver
          </Typography>
          {showAuthActions && username ? (
            <Typography variant="body2" sx={{ mr: 1 }}>
              {username}
            </Typography>
          ) : null}
          {showAuthActions ? (
            <Button color="inherit" size="small" onClick={handleLogout} sx={{ mr: 1 }}>
              Esci
            </Button>
          ) : null}
          <ThemeModeSwitch />
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={open} onClose={() => setOpen(false)}>
        <NavigationList onNavigate={() => setOpen(false)} />
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, mt: 8, p: 2 }}>
        <Outlet />
      </Box>

      <Paper sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }} elevation={3}>
        <BottomNavigation
          showLabels
          value={current === -1 ? 0 : current}
          onChange={(_, value) => {
            navigate(visibleNavItems[value].path);
          }}
        >
          {visibleNavItems.map((item) => (
            <BottomNavigationAction key={item.path} label={item.label} icon={item.icon} />
          ))}
        </BottomNavigation>
      </Paper>
    </Box>
  );
}

export function AppLayout() {
  const isDesktop = useMediaQuery("(min-width:960px)");

  return isDesktop ? <DesktopLayout /> : <MobileLayout />;
}

function ProtectedLayout() {
  const authRequired = useAuthStore((state) => state.authRequired);
  const authLoading = useAuthStore((state) => state.authLoading);
  const provider = useAuthStore((state) => state.provider);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());
  const canAccessPath = useAuthStore((state) => state.canAccessPath);
  const location = useLocation();

  if (authRequired === null || authLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (authRequired && !isAuthenticated) {
    if (provider === "keycloak") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
          <CircularProgress />
        </Box>
      );
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (authRequired && isAuthenticated && !canAccessPath(location.pathname)) {
    return <Navigate to="/search" replace />;
  }

  return <AppLayout />;
}

function RoleAwareHomeRoute() {
  const isLegalRestrictedUser = useAuthStore((state) => state.isLegalRestrictedUser());
  if (isLegalRestrictedUser) {
    return <Navigate to="/search" replace />;
  }
  return <Dashboard />;
}

function LegalAllowedRoute({ element }: { element: JSX.Element }) {
  const isLegalRestrictedUser = useAuthStore((state) => state.isLegalRestrictedUser());
  const location = useLocation();
  if (isLegalRestrictedUser && !isLegalAllowedPath(location.pathname)) {
    return <Alert severity="warning">Accesso negato: ruolo legal abilitato solo a Ricerca, Archivio e Profilo.</Alert>;
  }
  return element;
}

function ManageOnlyRoute({ element }: { element: JSX.Element }) {
  const canManageSettings = useAuthStore((state) => state.canManageSettings());
  if (!canManageSettings) {
    return <Alert severity="warning">Accesso negato: ruolo operatore in sola lettura.</Alert>;
  }
  return element;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<ProtectedLayout />}>
        <Route index element={<RoleAwareHomeRoute />} />
        <Route path="stats" element={<LegalAllowedRoute element={<Statistics />} />} />
        <Route path="archive" element={<LegalAllowedRoute element={<ArchiveBrowser />} />} />
        <Route path="search" element={<LegalAllowedRoute element={<SearchCenter />} />} />
        <Route path="notifications" element={<LegalAllowedRoute element={<NotificationsCenter />} />} />
        <Route path="activity" element={<ManageOnlyRoute element={<ActivityStream />} />} />
        <Route path="profile" element={<LegalAllowedRoute element={<Profile />} />} />
        <Route path="settings" element={<ManageOnlyRoute element={<Settings />} />} />
        <Route path="developers" element={<ManageOnlyRoute element={<DeveloperScaffolding />} />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}









